import {useOverlayActivity} from "components/useOverlayActivity";
import React, {useRef, useState} from "react";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import {Dialog, useTheme} from "@mui/material";
import BaseInput from "components/BaseInput";
import {EmailSuggestionChip} from "components/emailSuggestion/EmailSuggestionChip";
import {FeedbackTopicSelect} from "components/FeedbackTopicSelect";
import {useMessages} from "i18n";
import {overlay, shadows} from "theme/tokens";
import {SheetTransition} from "transitions/SheetTransition";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {useFeedback} from "./useFeedback";
import {
	type FeedbackFieldErrorKey,
	feedbackFieldErrorText,
} from "./feedbackValidation";
import {
	Actions,
	Body,
	CloseButton,
	ErrorText,
	Eyebrow,
	Field,
	FieldError,
	FieldHint,
	Fields,
	Form,
	GhostButton,
	Head,
	HeadInner,
	Lede,
	MessageCounter,
	Sheet,
	Spinner,
	SubmitButton,
	Success,
	SuccessMark,
	SuccessNote,
	SuccessTitle,
	Title,
	TitleAccent,
	TriggerButton,
} from "./Feedback.styles";

/** Stable ids, so each field can point at its own hint and error through `aria-describedby`. */
const TOPIC_FIELD_ID = "feedback-topic";
const EMAIL_FIELD_ID = "feedback-email";
const EMAIL_HINT_ID = "feedback-email-hint";
const EMAIL_ERROR_ID = "feedback-email-error";
const MESSAGE_FIELD_ID = "feedback-message";
const MESSAGE_COUNTER_ID = "feedback-message-counter";
const MESSAGE_ERROR_ID = "feedback-message-error";

/** Joins the description ids a field actually has; an id that describes nothing is worse than none. */
function describedBy(...ids: (string | false)[]): string | undefined {
	const present = ids.filter((id): id is string => id !== false);
	return present.length > 0 ? present.join(" ") : undefined;
}

interface FeedbackProps {
	/**
	 * Optional custom trigger. Receives an `open` callback so callers can render any clickable
	 * element (e.g. a popover/menu item) instead of the default chat IconButton, while reusing the
	 * exact same feedback dialog. When omitted, the original white chat-bubble IconButton is used.
	 */
	trigger?: (open: () => void) => React.ReactNode;
	/** Replaces MUI's implicit restore target when a custom trigger disappears while the dialog is open. */
	onTransitionExited?: () => void;
}

/**
 * The Support/Feedback dialog (ui#149) — a dumb, restyleable view over the {@link useFeedback}
 * controller (epic #26). It renders the editorial head (mono eyebrow · Instrument-Serif title with
 * an italic accent · lede), the labeled house inputs, and swaps to a calm in-dialog "Thank you"
 * moment after a send. All copy comes from the `feedback` i18n namespace; all colors from
 * `theme.tokens`. Open/close state lives here; callers may customize the trigger and focus restoration.
 */
export default function Feedback({trigger, onTransitionExited}: FeedbackProps = {}) {
	const m = useMessages();
	const {tokens} = useTheme();
	const [open, setOpen] = useState(false);
	const observedExit = useOverlayActivity(open, onTransitionExited);
	const feedback = useFeedback();
	const sendButton = useRef<HTMLButtonElement>(null);

	// Keyboard focus must stay inside the dialog for the whole send (kitecorp/transferify-ui#3079). The
	// fields are disabled while a send is in flight, and a disabled element cannot hold focus: the
	// browser moves it to the page body, where Escape reaches nothing until MUI's focus trap pulls it
	// back on its next 50 ms tick. The Send button is the one control that stays focusable throughout
	// (see its `aria-disabled` below), so a send started from the reply-to field with Enter, or by a
	// click that did not focus the button (Safari's default), moves focus there. A layout effect, so the
	// move happens in the same task as the commit that disabled the field, before any key can arrive.
	useIsomorphicLayoutEffect(() => {
		if (feedback.submitting) {
			sendButton.current?.focus();
		}
	}, [feedback.submitting]);

	const handleOpen = () => setOpen(true);
	const handleClose = () => {
		setOpen(false);
		// Clear the success/error moment so the next open starts from a fresh form.
		feedback.reset();
	};

	// A real submit, so Enter in either field sends. `noValidate` because the browser's own
	// `type="email"` check is LOOSER than the mailbox grammar the api enforces (it accepts
	// `user@localhost`) and its bubble cannot be translated or placed at the field — one owned,
	// server-faithful error path beats two disagreeing ones (ui#326).
	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		void feedback.submit();
	};

	// The length quoted by a "too long" complaint is the budget the CHOSEN TOPIC leaves, not the raw
	// api bound — the field's own `maxLength` stops typing at that same number.
	const fieldErrorText = (key: FeedbackFieldErrorKey | null) =>
		key === null ? null : feedbackFieldErrorText(m.feedback.fieldErrors, key, feedback.maxMessageLength);
	const emailError = fieldErrorText(feedback.fieldErrors.replyTo);
	const messageError = fieldErrorText(feedback.fieldErrors.message);

	const dialog = (
		<Dialog
			open={open}
			onClose={handleClose}
			disableRestoreFocus={onTransitionExited !== undefined}
			maxWidth={false}
			slots={{transition: SheetTransition}}
			slotProps={{
				backdrop: {style: {background: overlay.scrim}},
				// `onExited` on the TRANSITION, never `onTransitionExited` on the Dialog: MUI runs the
				// latter BEFORE `ModalManager.remove` un-hides the background, so a restore wired there
				// aims at a still-`aria-hidden` page. See the same note in {@link components/ModalSheet}.
				transition: {onExited: observedExit},
				paper: {
					"aria-label": m.feedback.ariaLabel,
					style: {
						width: "460px",
						maxWidth: "calc(100% - 32px)",
						maxHeight: "calc(100% - 32px)",
						margin: 0,
						borderRadius: 24,
						backgroundColor: tokens.card,
						backgroundImage: "none",
						border: `1px solid ${tokens.cardBorder}`,
						boxShadow: shadows.modal,
						overflow: "hidden",
					},
				},
			}}
		>
			<Sheet>
				<CloseButton type="button" onClick={handleClose} aria-label={m.common.close}>✕</CloseButton>

				{feedback.submitted ? (
					<Body>
						<Success>
							<SuccessMark aria-hidden="true">✓</SuccessMark>
							<SuccessTitle>{m.feedback.success.title}</SuccessTitle>
							<SuccessNote>{m.feedback.success.note}</SuccessNote>
							{/* This view replaces the form, and the focused Send button with it, so focus
							    moves here as it mounts instead of dropping to the page (ui#3079). */}
							<SubmitButton type="button" onClick={handleClose} autoFocus>{m.common.done}</SubmitButton>
						</Success>
					</Body>
				) : (
					<>
						<Head>
							<HeadInner>
								<Eyebrow>{m.feedback.eyebrow}</Eyebrow>
								<Title>
									{m.feedback.title} <TitleAccent>{m.feedback.titleAccent}</TitleAccent>
								</Title>
								<Lede>{m.feedback.lede}</Lede>
							</HeadInner>
						</Head>

						<Body>
							<Form onSubmit={handleSubmit} noValidate>
								<Fields>
									{/* First, because it frames the two fields under it: the sender says what the
									    note is about before writing it. Optional — see FeedbackTopicSelect. */}
									<Field>
										<FeedbackTopicSelect
											id={TOPIC_FIELD_ID}
											value={feedback.topic}
											onChange={feedback.setTopic}
											disabled={feedback.submitting}
										/>
									</Field>

									<Field>
										<BaseInput
											id={EMAIL_FIELD_ID}
											label={m.feedback.emailLabel}
											type="email"
											value={feedback.email}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => feedback.setEmail(e.target.value)}
											disabled={feedback.submitting}
											error={emailError !== null}
											aria-describedby={describedBy(EMAIL_HINT_ID, emailError !== null && EMAIL_ERROR_ID)}
										/>
										{/* ui#1959 — a reply-to nobody can reply to is the whole cost of a typo here. */}
										<EmailSuggestionChip value={feedback.email} onAccept={feedback.setEmail}/>
										<FieldHint id={EMAIL_HINT_ID}>{m.feedback.emailHint}</FieldHint>
										{emailError && <FieldError id={EMAIL_ERROR_ID} role="alert">{emailError}</FieldError>}
									</Field>

									<Field>
										<BaseInput
											id={MESSAGE_FIELD_ID}
											label={m.feedback.messageLabel}
											value={feedback.message}
											onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => feedback.setMessage(e.target.value)}
											placeholder={m.feedback.messagePlaceholder}
											disabled={feedback.submitting}
											error={messageError !== null}
											aria-describedby={describedBy(MESSAGE_COUNTER_ID, messageError !== null && MESSAGE_ERROR_ID)}
											multiline
											minRows={4}
											maxRows={7}
											// Stops the sender AT the api's bound instead of letting them compose a
											// note that comes back as a 400 (FeedbackRequest `@Size(max = 512)`).
											// The bound MINUS the chosen topic's inbox tag, because the tag is part
											// of the body the api measures.
											slotProps={{input: {maxLength: feedback.maxMessageLength}}}
										/>
										<MessageCounter
											id={MESSAGE_COUNTER_ID}
											$atLimit={feedback.message.length >= feedback.maxMessageLength}
											aria-label={m.feedback.messageCounterLabel(feedback.maxMessageLength)}
										>
											{m.feedback.messageCounter(feedback.message.length, feedback.maxMessageLength)}
										</MessageCounter>
										{messageError && <FieldError id={MESSAGE_ERROR_ID} role="alert">{messageError}</FieldError>}
									</Field>
								</Fields>

								{feedback.error && <ErrorText role="alert">{m.feedback.errors.sendFailed}</ErrorText>}

								<Actions>
									<GhostButton type="button" onClick={handleClose}>{m.common.cancel}</GhostButton>
									{/* DISABLED only for an incomplete draft, which also stops Enter from submitting
									    it. While sending it is `aria-disabled` instead: still focusable, so focus
									    never leaves the dialog, and a press joins the send already in flight. */}
									<SubmitButton
										ref={sendButton}
										type="submit"
										disabled={!feedback.draftComplete}
										aria-disabled={feedback.submitting || undefined}
									>
										{feedback.submitting
											? <><Spinner aria-hidden="true"/>{m.feedback.submitting}</>
											: m.feedback.submit}
									</SubmitButton>
								</Actions>
							</Form>
						</Body>
					</>
				)}
			</Sheet>
		</Dialog>
	);

	// Custom trigger (e.g. an account-popover "Support" menu item): render it alongside the shared
	// dialog. Otherwise fall back to the default white chat-bubble button used in the navigation rail.
	if (trigger) {
		return <>{dialog}{trigger(handleOpen)}</>;
	}

	return (
		<>
			{dialog}
			<TriggerButton type="button" aria-label={m.feedback.triggerAria} onClick={handleOpen}>
				<ChatBubbleIcon/>
			</TriggerButton>
		</>
	);
}
